import showConfirmationDialog from 'app/main/apps/common/AuraSimpleConfirmDialogs';

/**
 * PGSS-5702: one handler for every screen. When staff send a quote for approval or approve it while lines have no
 * price, the API answers 409 { code: 'UNPRICED_LINES', items } and saves nothing. This shows the items and, on
 * "Send anyway", repeats the same request with confirm_unpriced=true. "Go back" leaves the order unchanged.
 */

export const UNPRICED_LINES_CODE = 'UNPRICED_LINES';
export const UNPRICED_CONFIRM_PARAM = 'confirm_unpriced';

export function isUnpricedLinesReply(error) {
  return error?.response?.status === 409 && error?.response?.data?.code === UNPRICED_LINES_CODE;
}

export function unpricedLinesMessage(items) {
  const names = Array.isArray(items) && items.length ? items.join(', ') : 'some items';
  return `These items have no price: ${names}. The customer will see them without a price.`;
}

/** Asks staff; resolves true for "Send anyway", false for "Go back" or closing the dialog. */
export function confirmUnpricedLines(dispatch, items) {
  return new Promise((resolve) => {
    showConfirmationDialog(dispatch, {
      title: 'Some items have no price',
      message: unpricedLinesMessage(items),
      displayCancelBtn: true,
      cancelActionName: 'Go back and add prices',
      actionName: 'Send anyway',
      // The dialog reports its main button as 'submit' and its Cancel button as 'close'.
      onActionBtnClick: (action) => resolve(action === 'submit'),
      onCancelBtnClick: () => resolve(false),
    });
  });
}

let installed = false;

/**
 * Adds the response handler to an axios instance once. {@code confirm(items)} returns a Promise of true/false.
 * A request already sent with confirm_unpriced is never asked about again.
 */
export function installUnpricedLinesWarning(axiosInstance, confirm) {
  if (installed) return;
  installed = true;
  axiosInstance.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    if (!isUnpricedLinesReply(error) || !config || config.params?.[UNPRICED_CONFIRM_PARAM]) {
      return Promise.reject(error);
    }
    const sendAnyway = await confirm(error.response.data.items || []);
    if (!sendAnyway) {
      return Promise.reject(error);
    }
    return axiosInstance.request({ ...config, params: { ...(config.params || {}), [UNPRICED_CONFIRM_PARAM]: true } });
  });
}

/** For tests only. */
export function resetUnpricedLinesWarningForTests() {
  installed = false;
}
